import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Smartphone, Globe, Zap, Users, TrendingUp, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { companyInfo, services, portfolio, testimonials, stats } from '../mock';

const Home = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 mb-4">
                <span className="px-4 py-2 bg-gray-100 text-black text-sm font-medium rounded-full">
                  ✓ Verified FlutterFlow Agency
                </span>
              </div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-black mb-6 leading-tight">
                End-to-End Digital Solutions for Modern
                <span className="block mt-2">Businesses</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                {companyInfo.tagline}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button
                    size="lg"
                    className="bg-black text-white hover:bg-gray-800 transition-all duration-300 hover:scale-105 group"
                  >
                    Start Your Project
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/portfolio">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-2 border-black text-black hover:bg-black hover:text-white transition-all duration-300"
                  >
                    View Our Work
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative invisible md:visible h-0 md:h-auto">
              <div className="relative rounded-lg overflow-hidden shadow-2xl">
                <img
                  src="https://raw.githubusercontent.com/wwwjonnyfaxan786-hash/portfolio-assets/main/Codify%20Team%20Photo.jpg"
                  alt="Codify Team Photo"
                  decoding="async"
                  className="w-full h-auto object-cover"
                  fetchpriority="high"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-gray-100 rounded-full filter blur-2xl opacity-70 -z-10"></div>
              <div className="absolute -top-6 -right-6 w-40 h-40 bg-gray-100 rounded-full filter blur-2xl opacity-70 -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold mb-2">{stat.value}</div>
                <div className="text-gray-400 text-sm md:text-base">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">
              Our Services
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive app development solutions tailored to your needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.slice(0, 6).map((service) => (
              <Card
                key={service.id}
                className="group hover:shadow-2xl transition-all duration-300 border-2 border-gray-100 hover:border-black cursor-pointer"
              >
                <CardContent className="p-6">
                  <div className="mb-4">
                    <div className="w-12 h-12 bg-black text-white rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Check className="w-6 h-6" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-black mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.slice(0, 3).map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-600">
                        <Check className="w-4 h-4 mr-2 text-black" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-black text-black hover:bg-black hover:text-white transition-all duration-300"
              >
                View All Services
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">
              Why Choose CodifyTech?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Verified FlutterFlow agency delivering excellence in every project
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Award,
                title: 'Unmatched FlutterFlow Expertise, Proven at Scale',
                description: 'We’ve built and launched 20+ FlutterFlow apps live across iOS, Android, and Web'
              },
              {
                icon: Zap,
                title: 'Full-Stack Development',
                description: 'End-to-end development using FlutterFlow, React Native, Swift, Firebase, and modern cloud technologies.'
              },
              {
                icon: Users,
                title: 'Client-Focused',
                description: 'Transparent communication, agile collaboration, and solutions tailored to real business needs—not templates.'
              },
              {
                icon: Globe,
                title: 'Cross-Platform',
                description: 'Build once, deploy everywhere—iOS, Android, Web, and custom platforms with consistent performance and UX.'
              },
              {
                icon: TrendingUp,
                title: 'Scalable Solutions',
                description: 'Cloud-native architectures using Firebase, Google Cloud Functions, and automation-ready systems built to grow.'
              },
              {
                icon: Smartphone,
                title: 'Advanced Features',
                description: 'AI automation, workflows, real-time data, integrations, analytics, and performance optimization.'
              }
            ].map((item, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-lg hover:shadow-xl transition-all duration-300 border border-gray-200"
              >
                <item.icon className="w-12 h-12 text-black mb-4" />
                <h3 className="text-xl font-bold text-black mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack Section */}
<section className="hidden md:block py-20 px-4 sm:px-6 lg:px-8 bg-black text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Our Tech Stack
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Powerful technologies for building exceptional applications
            </p>
          </div>

          <div className="grid grid-cols-3 md:grid-cols-4 gap-8">
            {[
              { name: 'FlutterFlow', desc: 'Visual Development' },
              { name: 'AI & Automation', desc: 'Workflows & Intelligent Systems' },
              { name: 'Flutter', desc: 'High-performance apps with a single, powerful codebase.' },
              { name: 'React.js', desc: 'Dynamic, scalable interfaces built for modern web.' },
              { name: 'Vue.js', desc: 'Elegant, lightweight frontends with exceptional performance.' },
              { name: 'Next.js', desc: 'Server-rendered React for lightning-fast, SEO-ready apps.' },
              { name: 'Java', desc: 'Enterprise-grade backend systems built to scale securely.' },
              { name: 'Node.js', desc: 'High-performance, real-time applications powered by JavaScript.' },
              { name: 'PHP (Laravel)', desc: 'Clean, secure, and rapid backend development.' },
              { name: 'Python (Django, Flask, FastAPI)', desc: 'Powerful, flexible backends for data-driven applications.' },
              { name: 'Amazon Web Services (AWS)', desc: 'Scalable, secure, and cloud-native infrastructure.' },
              { name: 'Microsoft Azure', desc: 'Enterprise cloud solutions with global reliability.' }
            ].map((tech, index) => (
              <div
                key={index}
                className="text-center p-6 bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors duration-300"
              >
                <div className="text-2xl font-bold mb-2">{tech.name}</div>
                <div className="text-sm text-gray-400">{tech.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Preview */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">
              Featured Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Explore our successful app development projects
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {portfolio.slice(0, 3).map((project) => (
              <Card
                key={project.id}
                onClick={() => {
                  if (project.applink) {
                    window.open(project.applink, "_blank", "noopener,noreferrer");
                  } 
                }}
                title="Click to open app"
                className="group hover:shadow-2xl transition-all duration-300 overflow-hidden border-2 border-gray-100 hover:border-black"
              >
                <div className="h-40 flex items-center justify-center bg-gray-50 rounded-xl mb-4">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="max w-[50%] max h- [40%] object-contain"
                  />
                </div>


                <CardContent className="p-6">
                  <div className="inline-block px-3 py-1 bg-gray-100 text-black text-xs font-medium rounded-full mb-3">
                    {project.category}
                  </div>
                  <h3 className="text-xl font-bold text-black mb-2">{project.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">{project.description}</p>
                  <div className="flex flex-wrap gap-2">
                   
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/portfolio">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-black text-black hover:bg-black hover:text-white transition-all duration-300"
              >
                View All Projects
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-black text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">What Our Clients Say</h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Don't just take our word for it
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-gray-900 p-6 rounded-lg hover:bg-gray-800 transition-colors duration-300"
              >
                <p className="text-gray-300 mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <div className="font-bold">{testimonial.name}</div>
                  <div className="text-sm text-gray-400">
                    {testimonial.role} at {testimonial.company}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
            Ready to Build Your Next App?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Let’s bring your ideas to life with Codify Tech’s proven expertise.
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-black text-white hover:bg-gray-800 transition-all duration-300 hover:scale-105 group"
            >
              Get in Touch
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
